package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.CreatePlantingRequest
import fr.frux.shared.dto.CreateSiteReportRequest
import fr.frux.shared.dto.CreateSiteRequest
import fr.frux.shared.dto.SharePreviewDto
import fr.frux.shared.dto.SiteDto
import fr.frux.shared.dto.SiteListResponse
import fr.frux.shared.dto.SiteMembersResponse
import fr.frux.shared.dto.SiteRequestedRoleDto
import fr.frux.shared.dto.SiteRoleRequest
import fr.frux.shared.dto.UpdateSiteRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody

/**
 * Lieux de cueillette (ADR 0035). Les refus du serveur remontent en `ClientRequestException`
 * dont le corps porte le `code` d'`ErrorResponse`. Un client par ressource REST : le fractionner
 * éloignerait l'interface des routes qu'elle reflète.
 */
@Suppress("TooManyFunctions")
interface SiteApi {
    /** Lieux autour d'un point. `limit` est plafonné à 1000 côté serveur, `bbox` vaut `west,south,east,north`. */
    suspend fun getNearbySites(
        lat: Double,
        lon: Double,
        radius: Double = 5.0,
        limit: Int = 50,
        bbox: String? = null,
    ): SiteListResponse

    suspend fun getSite(id: Long): SiteDto

    /** Aperçu public d'un lieu (GEO), sans jeton. 404 `SITE_NOT_FOUND` si le lieu est masqué ou discret. */
    suspend fun sharePreview(id: Long): SharePreviewDto

    /** [idempotencyKey] est rejoué en `Idempotency-Key` quand il est fourni. */
    suspend fun createSite(
        request: CreateSiteRequest,
        idempotencyKey: String? = null,
    ): SiteDto

    suspend fun updateSite(
        id: Long,
        request: UpdateSiteRequest,
    ): SiteDto

    /** Créateur seul, tant qu'aucun autre compte n'y a rattaché d'arbre. Les arbres sont détachés. */
    suspend fun deleteSite(id: Long)

    /** Revendique le lieu (PRIVATE). 403 `CLAIM_FORBIDDEN`, 409 `ALREADY_OWNED`. */
    suspend fun claimSite(id: Long): SiteDto

    /** Libère le lieu (ASK). */
    suspend fun unclaimSite(id: Long): SiteDto

    /** Le serveur rend le lieu complet (201), pas la seule plantation créée. 409 `PLANTING_EXISTS`. */
    suspend fun addPlanting(
        siteId: Long,
        request: CreatePlantingRequest,
    ): SiteDto

    suspend fun removePlanting(
        siteId: Long,
        plantingId: Long,
    )

    /** 403 `ATTACH_FORBIDDEN`, 409 `ATTACH_TOO_FAR` ou `TREE_ALREADY_ATTACHED`. */
    suspend fun attachTree(
        siteId: Long,
        treeId: Long,
    ): SiteDto

    suspend fun detachTree(
        siteId: Long,
        treeId: Long,
    ): SiteDto

    /** Idempotent : 201 la première fois, 200 ensuite, les deux sont des succès. */
    suspend fun favoriteSite(id: Long)

    suspend fun unfavoriteSite(id: Long)

    suspend fun getFavoriteSites(
        page: Int = 1,
        limit: Int = 50,
    ): SiteListResponse

    /** 403 `SELF_REPORT`, 409 `ALREADY_REPORTED`, 429 `REPORT_QUOTA_EXCEEDED`. */
    suspend fun reportSite(
        id: Long,
        request: CreateSiteReportRequest,
    ): SiteDto

    /** Demande un rôle sur un jardin collectif (ADR 0037). 409 `NOT_COLLECTIVE_GARDEN` ou `ALREADY_MEMBER`,
     *  429 `MANAGER_REQUEST_LIMIT`, 400 `MESSAGE_TOO_LONG`. `GARDENER` sans gestionnaire devient `GARDENER_SELF`. */
    suspend fun requestSiteRole(
        id: Long,
        role: SiteRequestedRoleDto,
        message: String? = null,
    ): SiteDto

    /** Retire sa demande ou quitte le rôle. */
    suspend fun leaveSiteRole(
        id: Long,
        role: SiteRequestedRoleDto,
    ): SiteDto

    /** Membres du jardin, gestionnaires seuls : 403 `SITE_FORBIDDEN` sinon. */
    suspend fun getSiteMembers(id: Long): SiteMembersResponse

    /** Valide un jardinier en attente. 403 `SITE_FORBIDDEN`, 404 `MEMBER_NOT_FOUND`. */
    suspend fun acceptSiteMember(
        id: Long,
        userId: Long,
    ): SiteMembersResponse

    /** Retire un jardinier, ses récoltes à venir sur le lieu sont annulées. */
    suspend fun removeSiteMember(
        id: Long,
        userId: Long,
    ): SiteMembersResponse
}

/** Une fonction par endpoint, même compte que [SiteApi] : pas de baseline detekt. */
@Suppress("TooManyFunctions")
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class SiteApiImpl(
    private val httpClient: HttpClient,
) : SiteApi {
    override suspend fun getNearbySites(
        lat: Double,
        lon: Double,
        radius: Double,
        limit: Int,
        bbox: String?,
    ): SiteListResponse =
        httpClient
            .get("${ApiPaths.V1}/sites") {
                parameter("lat", lat)
                parameter("lon", lon)
                parameter("radius", radius)
                parameter("limit", limit)
                bbox?.let { parameter("bbox", it) }
            }.body()

    override suspend fun getSite(id: Long): SiteDto = httpClient.get("${ApiPaths.V1}/sites/$id").body()

    override suspend fun sharePreview(id: Long): SharePreviewDto =
        httpClient.get("${ApiPaths.V1}/sites/$id/share-preview").body()

    override suspend fun createSite(
        request: CreateSiteRequest,
        idempotencyKey: String?,
    ): SiteDto =
        httpClient
            .post("${ApiPaths.V1}/sites") {
                idempotencyKey?.let { header("Idempotency-Key", it) }
                setBody(request)
            }.body()

    override suspend fun updateSite(
        id: Long,
        request: UpdateSiteRequest,
    ): SiteDto =
        httpClient
            .patch("${ApiPaths.V1}/sites/$id") {
                setBody(request)
            }.body()

    override suspend fun deleteSite(id: Long) {
        httpClient.delete("${ApiPaths.V1}/sites/$id")
    }

    override suspend fun claimSite(id: Long): SiteDto = httpClient.post("${ApiPaths.V1}/sites/$id/claim").body()

    override suspend fun unclaimSite(id: Long): SiteDto = httpClient.delete("${ApiPaths.V1}/sites/$id/claim").body()

    override suspend fun addPlanting(
        siteId: Long,
        request: CreatePlantingRequest,
    ): SiteDto =
        httpClient
            .post("${ApiPaths.V1}/sites/$siteId/plantings") {
                setBody(request)
            }.body()

    override suspend fun removePlanting(
        siteId: Long,
        plantingId: Long,
    ) {
        httpClient.delete("${ApiPaths.V1}/sites/$siteId/plantings/$plantingId")
    }

    override suspend fun attachTree(
        siteId: Long,
        treeId: Long,
    ): SiteDto = httpClient.post("${ApiPaths.V1}/sites/$siteId/trees/$treeId").body()

    override suspend fun detachTree(
        siteId: Long,
        treeId: Long,
    ): SiteDto = httpClient.delete("${ApiPaths.V1}/sites/$siteId/trees/$treeId").body()

    override suspend fun favoriteSite(id: Long) {
        httpClient.post("${ApiPaths.V1}/sites/$id/favorite")
    }

    override suspend fun unfavoriteSite(id: Long) {
        httpClient.delete("${ApiPaths.V1}/sites/$id/favorite")
    }

    override suspend fun getFavoriteSites(
        page: Int,
        limit: Int,
    ): SiteListResponse =
        httpClient
            .get("${ApiPaths.V1}/sites/favorites") {
                parameter("page", page)
                parameter("limit", limit)
            }.body()

    override suspend fun reportSite(
        id: Long,
        request: CreateSiteReportRequest,
    ): SiteDto =
        httpClient
            .post("${ApiPaths.V1}/sites/$id/reports") {
                setBody(request)
            }.body()

    override suspend fun requestSiteRole(
        id: Long,
        role: SiteRequestedRoleDto,
        message: String?,
    ): SiteDto =
        httpClient
            .post("${ApiPaths.V1}/sites/$id/members/me") {
                setBody(SiteRoleRequest(role, message))
            }.body()

    override suspend fun leaveSiteRole(
        id: Long,
        role: SiteRequestedRoleDto,
    ): SiteDto =
        httpClient
            .delete("${ApiPaths.V1}/sites/$id/members/me") {
                parameter("role", role.name)
            }.body()

    override suspend fun getSiteMembers(id: Long): SiteMembersResponse =
        httpClient.get("${ApiPaths.V1}/sites/$id/members").body()

    override suspend fun acceptSiteMember(
        id: Long,
        userId: Long,
    ): SiteMembersResponse = httpClient.post("${ApiPaths.V1}/sites/$id/members/$userId/accept").body()

    override suspend fun removeSiteMember(
        id: Long,
        userId: Long,
    ): SiteMembersResponse = httpClient.delete("${ApiPaths.V1}/sites/$id/members/$userId").body()
}
